import { decodeHex, encodeHex } from './hexService'

const RSA_ALGORITHM = 'RSA-OAEP'
const RSA_HASH = 'SHA-256'
const PEM_HEADER = '-----BEGIN PUBLIC KEY-----\n'
const PEM_FOOTER = '\n-----END PUBLIC KEY-----'

/**
 * RsaService provides RSA key generation, encryption/decryption and key export/import.
 */
export class RsaService {
  /**
   * Generates an RSA key pair.
   * @returns A Promise that resolves to a CryptoKeyPair.
   */
  async generateRsaKeyPair(): Promise<CryptoKeyPair> {
    return crypto.subtle.generateKey(
      {
        name: RSA_ALGORITHM,
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: RSA_HASH,
      },
      true,
      ['encrypt', 'decrypt'],
    )
  }

  /**
   * Imports a PEM encoded public key string.
   * @param pemPublicKey - The PEM formatted public key.
   * @returns A Promise that resolves to a CryptoKey.
   */
  async getPublicKeyFromString(pemPublicKey: string): Promise<CryptoKey> {
    // Remove header, footer, and whitespace.
    const pemContents = pemPublicKey
      .replace(PEM_HEADER, '')
      .replace(PEM_FOOTER, '')
      .replace(/\s/g, '')
    const binaryDerString = atob(pemContents)
    const binaryDer = this.str2ArrayBuffer(binaryDerString)
    return crypto.subtle.importKey(
      'spki',
      binaryDer,
      { name: RSA_ALGORITHM, hash: RSA_HASH },
      true,
      ['encrypt'],
    )
  }

  /**
   * Encrypts plain text using the RSA public key.
   * @param plainText - The text to encrypt.
   * @param rsaPublicKey - The RSA public key.
   * @returns A Promise that resolves to the hex encoded cipher text.
   */
  async encrypt(plainText: string, rsaPublicKey: CryptoKey): Promise<string> {
    const encodedText = new TextEncoder().encode(plainText)
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: RSA_ALGORITHM },
      rsaPublicKey,
      encodedText,
    )
    return encodeHex(new Uint8Array(encryptedBuffer))
  }

  /**
   * Decrypts a hex encoded cipher text using the RSA private key.
   * @param cipherText - The hex encoded cipher text.
   * @param rsaPrivateKey - The RSA private key.
   * @returns A Promise that resolves to the decrypted plain text.
   */
  async decrypt(cipherText: string, rsaPrivateKey: CryptoKey): Promise<string> {
    const encryptedData = decodeHex(cipherText)
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: RSA_ALGORITHM },
      rsaPrivateKey,
      encryptedData,
    )
    return new TextDecoder().decode(decryptedBuffer)
  }

  /**
   * Exports the RSA public key to a PEM formatted string.
   * @param publicKey - The RSA public key.
   * @returns A Promise that resolves to the PEM encoded public key.
   */
  async exportPublicKey(publicKey: CryptoKey): Promise<string> {
    const exported = await crypto.subtle.exportKey('spki', publicKey)
    const binaryString = this.arrayBufferToString(exported)
    const base64String = btoa(binaryString)
    return PEM_HEADER + base64String + PEM_FOOTER
  }

  /**
   * Converts an ArrayBuffer to a string.
   * @param buffer - The input ArrayBuffer.
   * @returns The string representation.
   */
  private arrayBufferToString(buffer: ArrayBuffer): string {
    return String.fromCharCode(...new Uint8Array(buffer))
  }

  /**
   * Converts a string to an ArrayBuffer.
   * @param str - The input string.
   * @returns The ArrayBuffer representation.
   */
  private str2ArrayBuffer(str: string): ArrayBuffer {
    const buf = new ArrayBuffer(str.length)
    const bufView = new Uint8Array(buf)
    for (let i = 0; i < str.length; i++) {
      bufView[i] = str.charCodeAt(i)
    }
    return buf
  }
}

export const rsaService = new RsaService()
